import React from 'react';

import { useAppDispatch, useAppSelector, useAppStore } from '@/lib/hooks';
import {
  addQuestion,
  setQuizName,
  deleteQuestionAndUpdateAnswers,
  setActiveQuestionIndex,
} from '@/lib/slices/quiz-slice';

import QuizQuestionBuilder from './QuizQuestionBuilder';
import QuizGenModal from '@/common/components/modals/custom/content/QuizGenModal';
import QuizQuestionBuilderStatic from './QuizQuestionBuilderStatic';
import Button from '@/common/components/ui/Button';
import StandardInputText from '@/common/components/input/StandardInputText';

import QuizBuilderService from '@/services/course/creation/quiz-builder-service';
import { QuizQuestionData } from 'quiz-types';
import { AddIcon, CopyIcon, DeleteIcon } from '@/assets/icons/button';

function QuizBuilder(): React.JSX.Element {
  const store = useAppStore();
  const dispatch = useAppDispatch();
  const questions: Array<QuizQuestionData> = useAppSelector(() => store.getState().quiz.questions);
  const activeQuestionIndex: number = useAppSelector(
    () => store.getState().quiz.activeQuestionIndex,
  );
  const name: string = useAppSelector(() => store.getState().quiz.name);

  const handleNameChange = (name: string): void => {
    dispatch(setQuizName(name));
  };

  const handleNameChangeInput = (e: React.ChangeEvent<HTMLInputElement>): void => {
    handleNameChange(e.target.value);
  };

  const handleSelectQuestion = (index: number): void => {
    dispatch(setActiveQuestionIndex(index));
  };

  const addNewQuestion = () => {
    const newQuestion: QuizQuestionData = {
      index: questions.length,
      question: '',
      type: 'Multiple Choice',
      options: [{ index: 0, value: '' }],
    };
    dispatch(addQuestion({ index: newQuestion.index, question: newQuestion }));
    dispatch(setActiveQuestionIndex(newQuestion.index));
  };

  const deleteQuestion = (): void => {
    let newIndex = activeQuestionIndex - 1;
    if (activeQuestionIndex === 0) {
      newIndex = 0;
    }
    dispatch(deleteQuestionAndUpdateAnswers(activeQuestionIndex));
    dispatch(setActiveQuestionIndex(newIndex));
  };

  const copyQuestion = (): void => {
    const currentData = questions[activeQuestionIndex];
    const newQuestion: QuizQuestionData = {
      index: questions.length,
      question: currentData.question,
      type: currentData.type,
      options: currentData.options,
    };
    dispatch(setActiveQuestionIndex(newQuestion.index));
    dispatch(addQuestion({ index: newQuestion.index, question: newQuestion }));
  };

  return (
    <div className='flex flex-grow flex-col w-full py-4 gap-4'>
      <div className='flex flex-col gap-2 p-4 border border-border_gray_200 rounded shadow-sm hover:bg-surface_gray_25'>
        <StandardInputText
          id='quiz-title'
          label='Quiz Title'
          value={name}
          onChange={handleNameChangeInput}
          placeholderText='Quiz Title'
          isRequired
        />
      </div>
      <div>
        {questions.map((question) => (
          <div
            className='flex flex-col gap-2 mb-4 p-4 border border-border_gray_200 rounded shadow-sm hover:bg-surface_gray_25'
            key={question.index}
          >
            {activeQuestionIndex === question.index ? (
              <QuizQuestionBuilder index={question.index} />
            ) : (
              <QuizQuestionBuilderStatic
                onClick={(index: number) => handleSelectQuestion(index)}
                index={question.index}
                question={question.question}
                type={question.type}
                options={question.options}
              />
            )}
            {activeQuestionIndex === question.index ? (
              <>
                <span className='w-full border border-border_gray_200 self-center'></span>
                <div className='flex items-center justify-end gap-2'>
                  <Button
                    text={true}
                    className='w-8 h-8 p-1 rounded-lg self-end text-text_gray_500 hover:text-text_dark'
                    onClick={deleteQuestion}
                  >
                    <DeleteIcon />
                  </Button>
                  <Button
                    text={true}
                    className='w-8 h-8 p-1 rounded-lg self-end text-text_gray_400 hover:text-text_dark'
                    onClick={copyQuestion}
                  >
                    <CopyIcon />
                  </Button>
                </div>
              </>
            ) : null}
          </div>
        ))}
      </div>
      <div
        className={`${QuizBuilderService.isQuestionBlank(questions, activeQuestionIndex) && questions.length !== 0 ? 'hidden' : ''} self-center`}
      >
        <Button
          text={true}
          onClick={addNewQuestion}
          className='w-6 h-6 rounded-full border-2 border-text_gray_400 text-text_gray_400 hover:text-text_dark hover:border-text_dark'
        >
          <AddIcon />
        </Button>
      </div>
      <QuizGenModal />
    </div>
  );
}

export default QuizBuilder;
